import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { Auth } from './firebase/auth';
import { Observable, from, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { PUBLIC_API_REQUEST } from './public-api-request.context';

/**
 * Attaches a Firebase ID token as a Bearer token to every outgoing request
 * whose URL starts with the REST API base URL.
 *
 * Firebase ID tokens are short-lived JWTs (1 h) that the backend validates.
 * The AngularFire Auth SDK automatically refreshes them in the background so
 * `getIdToken()` always returns a valid token without any manual refresh logic.
 *
 * We must wait for `authStateReady()` before reading the user: on a cold start
 * (page reload) the auth state is initially unresolved, and acting on that
 * transient "no user" would send the request unauthenticated. For
 * optional-auth backend routes that silently degrades to the public response
 * instead of 401, so an authenticated user would see a truncated list (e.g.
 * archived/draft job postings missing). `authStateReady()` resolves only once
 * the persisted session has been restored.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly auth = inject(Auth);
  private readonly apiBaseUrl = environment.apiBaseUrl;
  private readonly authReady = this.auth.authStateReady();

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Only add the token to requests targeting the REST API backend.
    if (!req.url.startsWith(this.apiBaseUrl)) {
      return next.handle(req);
    }

    if (req.context.get(PUBLIC_API_REQUEST)) {
      return next.handle(req);
    }

    return from(this.authReady).pipe(
      switchMap(() => {
        const user = this.auth.currentUser;
        if (!user) {
          return next.handle(req);
        }
        return from(user.getIdToken()).pipe(
          switchMap(token => {
            const authedReq = req.clone({
              setHeaders: { Authorization: `Bearer ${token}` },
            });
            return next.handle(authedReq);
          })
        );
      })
    );
  }
}
